import React from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { ExperienceIds } from '~/acquisition/constants/experienceIds';
import { getTrendingProducts } from '~/searchCio/apis/getTrendingProducts';
import {
  CioOverlayProductsTitle,
  CioOverlayProduct,
  CioOverlayProductsSectionWrapper,
  CioOverlayProductsWrapper,
} from '~/searchCio/components/sharedComponents/productComponents';
import ProductCardsSkeletonLoader from '~/searchCio/components/sharedSections/ProductCardsSkeletonLoader';
import {
  PSRC_TRENDING_PRODUCTS,
  RECOMMENDATION_API_STALE_TIME,
} from '~/searchCio/constants/cioConstants';
import {
  productCardsDesktopParams,
  productCardsMobileParams,
} from '~/searchCio/constants/skeletonLoaderParams';
import { useCioClient } from '~/shared/utils/useCioClient';
import useGender from '~/shared/utils/useGender';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useMarketingDisposition } from '~/techstyle-shared/react-marketing';
import { useSession } from '~/techstyle-shared/redux-core';

const TrendingProductsHeader = () => {
  return (
    <CioOverlayProductsTitle role="heading" id="trending_products_title">
      <FormattedMessage
        id="search_autosuggest.trending_products_header"
        defaultMessage="Trending Products"
      />
    </CioOverlayProductsTitle>
  );
};

const TrendingProductsSection = () => {
  const cioClient = useCioClient();
  const { isMobile } = useBreakpoint();
  const { isVip, isElite } = useMembership();
  const isSecondarySite = useSecondarySite();
  const { gender } = useGender();
  const { isLoggedIn } = useSession();
  // get experienceId so that we can check if a user is in the scrubs post-reg
  // flow
  const disposition = useMarketingDisposition();
  const experienceId = disposition?.experience;
  const isScrubs =
    window.location.pathname.toLowerCase().includes('scrubs') ||
    experienceId === ExperienceIds.SCRUBS;

  const dynamicQueryParams = {
    isMobile,
    isVip,
    isElite,
    isSecondarySite,
    isLoggedIn,
    gender,
    isScrubs,
  };

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ['Trending Products', dynamicQueryParams],
    queryFn: () =>
      getTrendingProducts({
        cioClient,
        ...dynamicQueryParams,
      }),
    retry: 1,
    staleTime: RECOMMENDATION_API_STALE_TIME,
  });

  if ((isFetching && isLoading) || error || !data?.trendingProducts?.length) {
    const skeletonLoaderParams = isMobile
      ? productCardsMobileParams
      : productCardsDesktopParams;
    return <ProductCardsSkeletonLoader {...skeletonLoaderParams} />;
  }

  return (
    <CioOverlayProductsSectionWrapper
      data-cnstrc-recommendations
      data-cnstrc-recommendations-pod-id={data.podId}
      data-cnstrc-result-id={data.resultId}
      data-cnstrc-num-results={data.trendingProducts.length}
      data-testid="cnstrc-trending-products"
    >
      <TrendingProductsHeader />
      <CioOverlayProductsWrapper aria-labelledby="trending_products_title">
        {data.trendingProducts.map(({ strategy, data }, index) => {
          return (
            <CioOverlayProduct
              key={data.id}
              product={data}
              psrc={PSRC_TRENDING_PRODUCTS}
              dataTags={{
                'data-cnstrc-item': 'recommendation',
                'data-cnstrc-strategy-id': strategy.id,
                'data-testid': `cnstrc-trending-product-${index}`,
                'data-sku': data.sku,
              }}
            />
          );
        })}
      </CioOverlayProductsWrapper>
    </CioOverlayProductsSectionWrapper>
  );
};

TrendingProductsSection.propTypes = {
  indexOffset: PropTypes.number,
};

export default TrendingProductsSection;
